import type { OptionType } from "components/inputs";

type ExtendedOptionType = OptionType & {
  clientId?: string;
};

type GroupedQueryMap = Record<
  string,
  { include?: string[] } | { exclude?: string[] }
>;

export type FinanceFilters = {
  report_type: OptionType | null;
  posted_date_start: string | null;
  posted_date_end: string | null;
  clients: ExtendedOptionType[];
  client_search_term: string;
  service_types: OptionType[];
  client_service_map: GroupedQueryMap;
  invoice_statuses: OptionType[];
  file_ids: OptionType[];
  payment_status: OptionType | null;
  invoice_numbers: OptionType[];
  service_created_from: string | null;
  service_created_to: string | null;
  file_opened_on_from: string | null;
  file_opened_on_to: string | null;
  service_date_from: string | null;
  service_date_to: string | null;
  order_by: string | null;
  // Section 111 invoice filters (for Invoice List)
  icn: string | null;
  claim_id: string | null;
  claim_number: string | null;
  rre_name: string | null;
  rre_id: string | null;
  s111_claimant_name: string | null;
  // Section 111 Invoicing filters
  rre: OptionType | null;
  file_type: OptionType | null;
  hide_invoiced: boolean;
  nsa_filter: OptionType | null;
  action_filter: OptionType | null;
  send_date_from: string | null;
  send_date_to: string | null;
  doi_from: string | null;
  doi_to: string | null;
  tpoc_from: string | null;
  tpoc_to: string | null;
  medicare_from: string | null;
  medicare_to: string | null;
};

const SERVER_SIDE_SORT_REPORT_TYPES = new Set([
  "INVOICE_LIST",
  "INVOICE_LINE_ITEM_REPORT",
  "WORK_IN_PROGRESS",
  "AI_JOB_BILLING_REPORT",
  "SECTION_111_INVOICING",
  "BY_SERVICE_REPORT",
]);

// report type configurations
const REPORT_TYPE_CONFIG = {
  INVOICE_LIST: {
    backendType: "INVOICE_LIST",
    dateFromParam: "date_from",
    dateToParam: "date_to",
    defaultInvoiceStatus: null,
  },
  CLIENT_LIST_REPORT: {
    backendType: "CLIENT_LIST_REPORT",
    dateFromParam: "date_from",
    dateToParam: "date_to",
    defaultInvoiceStatus: null,
  },
  FILE_LIST: {
    backendType: "FILE_LIST",
    dateFromParam: "date_from",
    dateToParam: "date_to",
    defaultInvoiceStatus: null,
  },
  INVOICE_LINE_ITEM_REPORT: {
    backendType: "INVOICE_LINE_ITEM_REPORT",
    dateFromParam: "date_posted_from",
    dateToParam: "date_posted_to",
    defaultInvoiceStatus: null,
  },
  WORK_IN_PROGRESS: {
    backendType: "INVOICE_LINE_ITEM_REPORT",
    dateFromParam: "service_date_from",
    dateToParam: "service_date_to",
    defaultInvoiceStatus: ["Draft"], // always send Draft for WIP
  },
  INVOICE_PAYMENT_REPORT: {
    backendType: "INVOICE_PAYMENT_REPORT",
    dateFromParam: "payment_date_from",
    dateToParam: "payment_date_to",
    defaultInvoiceStatus: null,
  },
  BY_SERVICE_REPORT: {
    backendType: "BY_SERVICE_REPORT",
    dateFromParam: "date_posted_from",
    dateToParam: "date_posted_to",
    defaultInvoiceStatus: null,
  },
  BULK_BILLING_REPORT: {
    backendType: "BULK_BILLING_REPORT",
    dateFromParam: "date_posted_from",
    dateToParam: "date_posted_to",
    defaultInvoiceStatus: null,
  },
  REVENUE_BY_CLIENT_REPORT: {
    backendType: "REVENUE_BY_CLIENT_REPORT",
    dateFromParam: "date_from",
    dateToParam: "date_to",
    defaultInvoiceStatus: null,
  },
  NO_SERVICES_REPORT: {
    backendType: "NO_SERVICES_REPORT",
    dateFromParam: "date_from", // Not used for this report type
    dateToParam: "date_to", // Not used for this report type
    defaultInvoiceStatus: null,
    serviceCreatedFromParam: "service_created_from",
    serviceCreatedToParam: "service_created_to",
    fileOpenedFromParam: "file_opened_on_from",
    fileOpenedToParam: "file_opened_on_to",
  },
  EMPTY_FILES_REPORT: {
    backendType: "EMPTY_FILES_REPORT",
    dateFromParam: "date_from",
    dateToParam: "date_to",
    defaultInvoiceStatus: null,
  },
  AI_JOB_BILLING_REPORT: {
    backendType: "AI_JOB_BILLING_REPORT",
    dateFromParam: "service_date_from",
    dateToParam: "service_date_to",
    defaultInvoiceStatus: null,
  },
  SECTION_111_INVOICING: {
    backendType: "SECTION_111_INVOICING",
    dateFromParam: "date_from",
    dateToParam: "date_to",
    defaultInvoiceStatus: null,
    sendDateFromParam: "date_from",
    sendDateToParam: "date_to",
  },
} as const;

/**
 * builds invoice status array, handling Work in Progress special case
 */
const buildInvoiceStatus = (
  data: FinanceFilters,
  config: (typeof REPORT_TYPE_CONFIG)[keyof typeof REPORT_TYPE_CONFIG],
) => {
  // if report type has a default status (like Work in Progress -> Draft), use it
  if (config.defaultInvoiceStatus) {
    return { invoice_status: config.defaultInvoiceStatus };
  }

  // otherwise, use the selected statuses
  const validStatuses = data.invoice_statuses
    .map((status) => String(status.value))
    .filter(
      (value) =>
        value !== "undefined" && value !== "null" && value.trim() !== "",
    );

  return validStatuses.length > 0 ? { invoice_status: validStatuses } : {};
};

/**
 * builds the report options payload for API calls
 * handles the complex logic for different report types and parameter mapping
 */
export const buildReportOptions = (
  data: FinanceFilters,
  options?: { exportAsLedes?: boolean },
) => {
  // default to Invoice List if no report type is selected
  const reportType = String(data.report_type?.value || "INVOICE_LIST");
  const config =
    REPORT_TYPE_CONFIG[reportType as keyof typeof REPORT_TYPE_CONFIG] ||
    REPORT_TYPE_CONFIG.INVOICE_LIST;

  const invoiceStatusOptions = buildInvoiceStatus(data, config);

  const reportOptions = {
    report_type: config.backendType,
    options: {
      // date range parameters
      ...(data.posted_date_start && {
        [config.dateFromParam]: data.posted_date_start,
      }),
      ...(data.posted_date_end && {
        [config.dateToParam]: data.posted_date_end,
      }),

      // client filtering - send selected client database IDs (integers)
      // API accepts client.id (database ID) but returns client.client_id (string identifier)
      ...(data.clients &&
        data.clients.length > 0 && {
          client_ids: data.clients.map((client) =>
            parseInt(client.value.toString()),
          ),
        }),

      // service types filtering
      ...(data.service_types.length > 0 && {
        service_types: data.service_types.map((service) =>
          String(service.value),
        ),
      }),

      // service type rules
      ...(Object.keys(data.client_service_map).length > 0 && {
        client_service_map: data.client_service_map,
      }),

      // invoice status
      ...invoiceStatusOptions,

      // file IDs filtering
      ...(data.file_ids.length > 0 && {
        file_ids: data.file_ids.map((file) => parseInt(file.value.toString())),
      }),

      // payment status filtering
      ...(data.payment_status && {
        payment_status: String(data.payment_status.value),
      }),

      // invoice numbers filtering
      ...(data.invoice_numbers.length > 0 && {
        invoice_numbers: data.invoice_numbers.map((invoice) =>
          String(invoice.value),
        ),
      }),

      // Section 111 invoice filters
      ...(data.icn && { icn: data.icn }),
      ...(data.claim_id && { claim_id: data.claim_id }),
      ...(data.claim_number && { claim_number: data.claim_number }),
      // RRE name/id and S111 claimant name are scoped to INVOICE_LIST to avoid
      // colliding with the SECTION_111_INVOICING report's rre_id (database PK).
      ...(reportType === "INVOICE_LIST" && {
        ...(data.rre_name && { rre_name: data.rre_name }),
        ...(data.rre_id && { rre_id: data.rre_id }),
        ...(data.s111_claimant_name && {
          s111_claimant_name: data.s111_claimant_name,
        }),
      }),

      ...(data.service_created_from && {
        service_created_from: data.service_created_from,
      }),
      ...(data.service_created_to && {
        service_created_to: data.service_created_to,
      }),

      // FILE_LIST: map file_opened_on dates to date_from/date_to (filters by datetime_opened)
      ...(data.file_opened_on_from &&
        reportType === "FILE_LIST" && {
          date_from: data.file_opened_on_from,
        }),
      ...(data.file_opened_on_to &&
        reportType === "FILE_LIST" && {
          date_to: data.file_opened_on_to,
        }),
      // other reports (e.g. NO_SERVICES_REPORT): keep file_opened_on_from/to as-is
      ...(data.file_opened_on_from &&
        reportType !== "FILE_LIST" && {
          file_opened_on_from: data.file_opened_on_from,
        }),
      ...(data.file_opened_on_to &&
        reportType !== "FILE_LIST" && {
          file_opened_on_to: data.file_opened_on_to,
        }),

      ...(data.service_date_from && {
        service_date_from: data.service_date_from,
      }),
      ...(data.service_date_to && {
        service_date_to: data.service_date_to,
      }),

      // LEDES export option
      ...(options?.exportAsLedes && {
        export_as_ledes: true,
      }),

      // Section 111 Invoicing specific options
      ...(reportType === "SECTION_111_INVOICING" && {
        ...(data.rre?.value && { rre_id: Number(data.rre.value) }),
        ...(data.file_type && { file_type: String(data.file_type.value) }),
        hide_invoiced: data.hide_invoiced ?? true,
        ...(data.nsa_filter?.value && {
          nsa_filter: String(data.nsa_filter.value),
        }),
        ...(data.action_filter?.value && {
          action_filter: String(data.action_filter.value),
        }),
        ...(data.send_date_from && { date_from: data.send_date_from }),
        ...(data.send_date_to && { date_to: data.send_date_to }),
        ...(data.doi_from && { doi_from: data.doi_from }),
        ...(data.doi_to && { doi_to: data.doi_to }),
        ...(data.tpoc_from && { tpoc_from: data.tpoc_from }),
        ...(data.tpoc_to && { tpoc_to: data.tpoc_to }),
        ...(data.medicare_from && { medicare_from: data.medicare_from }),
        ...(data.medicare_to && { medicare_to: data.medicare_to }),
      }),

      // Server-side sorting
      ...(data.order_by && {
        order_by: data.order_by,
      }),
    },
  };

  return reportOptions;
};

/**
 * convert Table component sort columns to backend order_by format
 * Send frontend column names directly - backend handles the conversion
 */
export const convertSortToOrderBy = (
  sortColumns: readonly { columnKey: string; direction: "ASC" | "DESC" }[],
): string | null => {
  if (sortColumns.length === 0) {
    return null;
  }

  const { columnKey, direction } = sortColumns[0];
  // Send frontend column name directly - backend converts using its COLUMN_MAPPING
  return direction === "DESC" ? `-${columnKey}` : columnKey;
};

export const isServerSideSortingReport = (
  reportType: OptionType | null | undefined,
): boolean => {
  const value = reportType?.value?.toString();
  if (!value) {
    return false;
  }
  return SERVER_SIDE_SORT_REPORT_TYPES.has(value);
};

/**
 * report types supported by the finance system
 * these correspond to the backend report types and frontend report type options
 */
export enum ReportType {
  INVOICE_LIST = "INVOICE_LIST",
  CLIENT_LIST_REPORT = "CLIENT_LIST_REPORT",
  FILE_LIST = "FILE_LIST",
  INVOICE_LINE_ITEM_REPORT = "INVOICE_LINE_ITEM_REPORT",
  WORK_IN_PROGRESS = "WORK_IN_PROGRESS",
  INVOICE_PAYMENT_REPORT = "INVOICE_PAYMENT_REPORT",
  BY_SERVICE_REPORT = "BY_SERVICE_REPORT",
  BULK_BILLING_REPORT = "BULK_BILLING_REPORT",
  REVENUE_BY_CLIENT_REPORT = "REVENUE_BY_CLIENT_REPORT",
  NO_SERVICES_REPORT = "NO_SERVICES_REPORT",
  EMPTY_FILES_REPORT = "EMPTY_FILES_REPORT",
  AI_JOB_BILLING_REPORT = "AI_JOB_BILLING_REPORT",
  SECTION_111_INVOICING = "SECTION_111_INVOICING",
}

// All the complex key generation logic has been removed since the backend now provides unique IDs

/**
 * generates a descriptive filename when downloading finance reports
 */
export const generateReportFilename = (
  data: FinanceFilters,
  options?: { exportAsLedes?: boolean },
): string => {
  const reportType = String(data.report_type?.value || "INVOICE_LIST");
  const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, ""); // YYYYMMDD

  // start with report type in a clean format
  const reportName = reportType.toLowerCase().replace(/_/g, "-");
  const parts = [reportName];

  // add date range if specified (keep it concise)
  if (data.posted_date_start && data.posted_date_end) {
    const startDate = new Date(data.posted_date_start)
      .toISOString()
      .slice(5, 10)
      .replace(/-/g, ""); // MMDD
    const endDate = new Date(data.posted_date_end)
      .toISOString()
      .slice(5, 10)
      .replace(/-/g, ""); // MMDD
    parts.push(`${startDate}-${endDate}`);
  } else if (data.posted_date_start) {
    const startDate = new Date(data.posted_date_start)
      .toISOString()
      .slice(5, 10)
      .replace(/-/g, "");
    parts.push(`from-${startDate}`);
  } else if (data.posted_date_end) {
    const endDate = new Date(data.posted_date_end)
      .toISOString()
      .slice(5, 10)
      .replace(/-/g, "");
    parts.push(`until-${endDate}`);
  }

  // add filter indicators (keep it minimal)
  const filters = [];

  if (data.payment_status?.value) {
    filters.push(String(data.payment_status.value).toLowerCase());
  }

  if (data.invoice_statuses.length === 1) {
    filters.push(data.invoice_statuses[0].value.toString().toLowerCase());
  } else if (data.invoice_statuses.length > 1) {
    filters.push(`${data.invoice_statuses.length}status`);
  }

  // add client information to filename if present
  if (data.clients && data.clients.length > 0) {
    // use client IDs from selected clients in filename
    const clientIds = data.clients
      .map((client) => {
        const match = client.label.match(/^(\d+)\s*-/);
        return match ? match[1] : null;
      })
      .filter(Boolean);

    if (clientIds.length === 1) {
      filters.push(`client-${clientIds[0]}`);
    } else if (clientIds.length > 1) {
      filters.push(`${clientIds.length}clients`);
    }
  }

  if (data.file_ids.length === 1) {
    filters.push("1file");
  } else if (data.file_ids.length > 1) {
    filters.push(`${data.file_ids.length}files`);
  }

  if (data.service_types.length === 1) {
    // use a shortened version of service type for filename
    const serviceType = String(data.service_types[0].value)
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
    filters.push(serviceType);
  } else if (data.service_types.length > 1) {
    filters.push(`${data.service_types.length}services`);
  }

  if (filters.length > 0) {
    parts.push(filters.join("-"));
  }

  parts.push(timestamp);

  // use .txt extension for LEDES exports, .xlsx for regular reports
  const extension = options?.exportAsLedes ? ".txt" : ".xlsx";
  return `${parts.join("-")}${extension}`;
};

/**
 * computes which rows are eligible for selection in the Invoice List report,
 * enforcing status, "Valid for Posting", and bulk-group constraints.
 *
 * Pure function extracted from ReportTable's onSelectedRowsChange handler
 * so the logic can be unit-tested independently.
 */
export type InvoiceSelectionResult = {
  eligibleSelection: Set<string | number>;
  bulkViolation: boolean;
  shouldDeselectAll: boolean;
};

export const computeInvoiceSelection = (
  newSelection: Set<string | number>,
  selectedRows: Set<string | number>,
  reportData: Record<string, unknown>[],
): InvoiceSelectionResult => {
  // find which rows are newly added vs already selected
  const alreadySelected = new Set<string | number>();
  const newlyAdded = new Set<string | number>();
  selectedRows.forEach((key) => {
    if (newSelection.has(key)) alreadySelected.add(key);
  });
  newSelection.forEach((key) => {
    if (!selectedRows.has(key)) newlyAdded.add(key);
  });

  // determine the bulk_id group from rows that stay selected
  let lockedBulkId: string | null = null;
  let hasLockedNonBulk = false;
  alreadySelected.forEach((rowKey) => {
    const row = reportData.find((r) => getRowId(r) === rowKey);
    if (row) {
      const bulkId = String(row["Bulk ID"] || "");
      if (bulkId) lockedBulkId = bulkId;
      else hasLockedNonBulk = true;
    }
  });

  // always keep already-selected rows (deselection is unrestricted)
  const eligibleSelection = new Set<string | number>(alreadySelected);
  let bulkViolation = false;

  // only apply constraints to newly added rows
  newlyAdded.forEach((rowKey) => {
    const row = reportData.find((r) => getRowId(r) === rowKey);
    if (!row) return;

    const status = String(row.Status || "").toLowerCase();
    const paymentStatus = String(row["Payment Status"] || "").toLowerCase();
    const validForPosting = String(
      row["Valid for Posting"] || "",
    ).toLowerCase();

    // allow draft (if valid for posting) and finalized (for voiding)
    if (
      paymentStatus === "paid" ||
      (status === "draft" && validForPosting !== "yes") ||
      (status !== "draft" && status !== "finalized")
    ) {
      return;
    }

    // enforce single bulk_id group
    const bulkId = String(row["Bulk ID"] || "");
    if (lockedBulkId && bulkId !== lockedBulkId) {
      bulkViolation = true;
      return;
    }
    if (hasLockedNonBulk && bulkId) {
      bulkViolation = true;
      return;
    }

    eligibleSelection.add(rowKey);
  });

  // Header checkbox "select all" while rows are already selected:
  // deselect all instead of showing an error
  const shouldDeselectAll =
    bulkViolation && newlyAdded.size > 1 && selectedRows.size > 0;

  return { eligibleSelection, bulkViolation, shouldDeselectAll };
};

/**
 * gets a unique row ID from the backend, provided ID column
 *
 * @param row - the row data object with ID column from backend
 * @returns a string representation of the row's unique ID
 *
 */
export const getRowId = (row: Record<string, unknown>): string => {
  // check standard ID columns, then Section 111 columns
  if (row.ID !== undefined && row.ID !== null && row.ID !== "") {
    return String(row.ID);
  }
  if (row.id !== undefined && row.id !== null && row.id !== "") {
    return String(row.id);
  }
  // Section 111: "Detail IDs" (comma-separated) or "Claim ID" as row key
  if (
    row["Detail IDs"] !== undefined &&
    row["Detail IDs"] !== null &&
    row["Detail IDs"] !== ""
  ) {
    return String(row["Detail IDs"]);
  }
  if (
    row["Claim ID"] !== undefined &&
    row["Claim ID"] !== null &&
    row["Claim ID"] !== ""
  ) {
    return String(row["Claim ID"]);
  }
  return String(Math.random());
};
